import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';

import axios from 'axios';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, { IBrandingSpotApi } from '@services/BrandingsService';
import EventsService from '@services/EventsService';
import { env } from 'env';
import type { IBrandingSpotDriftRow } from '@shared/utils/mapTemplateDrift';

import {
  EditorFocus,
  EditorGroupId,
  EditorNodeKind,
  SpotType,
  getSpotType,
  FOCUS_TO_GROUP_IDS,
} from '../editorCategories';
import { useEditorMode, SortableListId } from '../EditorModeContext';
import {
  getSpotLocationPreviewUrl,
  getSpotThumbnailUrl,
  normalizeUeIdentifier,
} from '../spotContent';
import { useEditorStyles } from '../styles';
import { useDrift } from '../DriftContext';
import { useBrandingEntityCatalogue } from '../useBrandingEntityCatalogue';
import AiScriptsTreeSection from '../AiScripts/AiScriptsTreeSection';
import { useMapEditor } from '../MapEditorContext';
import { useCanUseMapEditor } from '../useCanUseMapEditor';
import { useSpawnableObjects } from '../useSpawnableObjects';
import { spawnRealtimeEntitiesInUnreal } from '../realtimeEntitiesUnreal';
import { readTriggerName, seedSpawnProperties } from '../triggerRegions';
import { isRealtimeEntityDirty } from '@interfaces/realtimeEntities';
import LayoutTree from '../MapEditor/LayoutTree';
import LayoutPlaque from '../MapEditor/LayoutPlaque';
import { sortByName } from '../sortUtils';
import SortToggleButton from '../SortToggleButton';
import ObjectsCsvActions from './ObjectsCsvActions';
import { useSceneTreeStyles } from './styles';

type AnySpot = Record<string, unknown> & {
  ueIdentifier?: string;
  name?: string;
  isActive?: boolean;
  type?: string;
};

const getSpotTypeIcon = (type: SpotType, sx: object) => {
  if (type === 'VIDEO') {
    return <PlayArrowOutlinedIcon sx={sx} />;
  }

  return <ImageOutlinedIcon sx={sx} />;
};

// The one sort-toggle button in the panel header follows whichever focus tab is on
// screen — 'environment' and 'mapEditor' have no alphabetically-sorted list.
const getSortableListIdForFocus = (
  focus: EditorFocus,
): SortableListId | null => {
  if (focus === 'spots') {
    return 'spots';
  }
  if (focus === 'hotspots') {
    return 'hotspots';
  }
  if (focus === 'objects') {
    return 'objects';
  }
  if (focus === 'assistant') {
    return 'aiScripts';
  }
  return null;
};

// Whether the list the header's sort button currently controls has anything to
// sort. Spots/hotspots/objects are counted locally; the AI scripts count lives in
// AiScriptsTreeSection, a different component, so that one comes from context.
const getIsCurrentSortListEmpty = (
  listId: SortableListId | null,
  spotsCount: number,
  spotDriftRowsCount: number,
  hotspotsCount: number,
  brandingEntitiesCount: number,
  isAiScriptsListEmpty: boolean,
): boolean => {
  if (listId === 'spots') {
    return spotsCount === 0 && spotDriftRowsCount === 0;
  }
  if (listId === 'hotspots') {
    return hotspotsCount === 0;
  }
  if (listId === 'objects') {
    return brandingEntitiesCount === 0;
  }
  if (listId === 'aiScripts') {
    return isAiScriptsListEmpty;
  }
  return true;
};

interface IRailButtonProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
  testId: string;
}

const RailButton = ({
  active,
  icon,
  label,
  tooltip,
  onClick,
  testId,
}: IRailButtonProps) => {
  const treeStyles = useSceneTreeStyles();
  return (
    <Tooltip title={tooltip} placement="right" arrow enterDelay={300}>
      <Box
        sx={[treeStyles.railItem, active && treeStyles.railItemActive]}
        onClick={onClick}
        role="tab"
        aria-label={tooltip}
        aria-selected={active}
        data-testid={testId}
      >
        {icon}
        <Box component="span" sx={treeStyles.railItemLabel}>
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
};

const SceneTree = () => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const treeStyles = useSceneTreeStyles();

  const {
    selectedNode,
    selectMapAndColors,
    selectSpot,
    selectHotspot,
    selectMapLayout,
    selectBrandingEntity,
    selectRealtimeEntity,
    activeFocus,
    setActiveFocus,
    isSceneTreeCollapsed,
    toggleSceneTree,
    setAutosaveStatus,
    highlightedSpotUeId,
    sortDirections,
    toggleSortDirection,
    isAiScriptsListEmpty,
    isSpotMediaUploadLocked,
  } = useEditorMode();

  const { rooms: layoutRooms } = useMapEditor();
  const canUseMapEditor = useCanUseMapEditor();

  const [collapsedGroups, setCollapsedGroups] = useState<
    Partial<Record<EditorGroupId, boolean>>
  >({});

  // Spot thumbnails whose image failed to load — fall back to the type icon.
  const [failedThumbs, setFailedThumbs] = useState<Record<string, boolean>>({});

  const toggleGroup = useCallback((id: EditorGroupId) => {
    setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const {
    roomSpots,
    setRoomSpots,
    brandingSpots,
    setBrandingSpots,
    brandingHotspots,
    setBrandingHotspots,
    currentRoom,
    currentBrandingId,
    isBrandingModeEnabled,
    setSelectedCustomizationUeId,
    setSelectedCustomizationTag,
    setSelectedCustomizationSpotType,
    setSelectedCustomizationTargetSize,
    setPendingHotspotPlacement,
    realtimeEntities,
    upsertRealtimeEntity,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { spotDrift } = useDrift();

  const driftByUe = useMemo(() => {
    const map = new Map<string, IBrandingSpotDriftRow<IBrandingSpotApi>>();
    spotDrift.rows.forEach((row) => {
      const ue = normalizeUeIdentifier(row.ueIdentifier);
      if (ue) {
        map.set(ue, row);
      }
    });
    return map;
  }, [spotDrift]);

  const handleSpotSelect = useCallback(
    (spot: AnySpot, ueId: string) => {
      const type = getSpotType(spot);
      setSelectedCustomizationUeId(ueId);
      setSelectedCustomizationTag(ueId);
      setSelectedCustomizationSpotType(type);

      setSelectedCustomizationTargetSize(null);
      selectSpot(ueId);
    },
    [
      selectSpot,
      setSelectedCustomizationUeId,
      setSelectedCustomizationTag,
      setSelectedCustomizationSpotType,
      setSelectedCustomizationTargetSize,
    ],
  );

  const spots = useMemo<AnySpot[]>(() => {
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    return Array.isArray(source) ? (source as AnySpot[]) : [];
  }, [currentBrandingId, isBrandingModeEnabled, brandingSpots, roomSpots]);

  const isMapColorsSelected =
    selectedNode?.kind === EditorNodeKind.MAP_AND_COLORS;

  const isMapLayoutSelected = selectedNode?.kind === EditorNodeKind.MAP_LAYOUT;

  const selectedSpotUeId =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? selectedNode.ueIdentifier
      : highlightedSpotUeId;

  const selectedNodeRef = useRef<HTMLDivElement | null>(null);
  const spotsCollapsed = !!collapsedGroups[EditorGroupId.SPOTS];

  useEffect(() => {
    if (!selectedSpotUeId) {
      return;
    }
    setCollapsedGroups((prev) => {
      if (!prev[EditorGroupId.SPOTS]) {
        return prev;
      }
      return { ...prev, [EditorGroupId.SPOTS]: false };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpotUeId]);

  useEffect(() => {
    if (!selectedSpotUeId || spotsCollapsed) {
      return;
    }
    const node = selectedNodeRef.current;
    if (node) {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedSpotUeId, spotsCollapsed, spots.length]);

  const handleToggleVisibility = useCallback(
    async (e: React.MouseEvent, spot: AnySpot) => {
      e.stopPropagation();
      const nextActive = spot.isActive === false;
      const useBrandingSpots = !!currentBrandingId && isBrandingModeEnabled;

      const brandingSpotId =
        typeof (spot as { brandingSpotId?: unknown }).brandingSpotId ===
        'string'
          ? ((spot as { brandingSpotId: string }).brandingSpotId as string)
          : null;
      const roomSpotId =
        typeof (spot as { roomSpotId?: unknown }).roomSpotId === 'string'
          ? ((spot as { roomSpotId: string }).roomSpotId as string)
          : null;
      const fallbackId =
        typeof (spot as { id?: unknown }).id === 'string'
          ? ((spot as { id: string }).id as string)
          : null;

      setAutosaveStatus('saving');
      try {
        if (useBrandingSpots && currentBrandingId) {
          const id = brandingSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing branding spot id for visibility toggle');
          }
          await BrandingsService.patchBrandingSpot(currentBrandingId, id, {
            isActive: nextActive,
          });
          const refreshed = await BrandingsService.getBrandingSpots(
            currentBrandingId,
            { limit: 200 },
          );
          setBrandingSpots(refreshed.spots ?? []);
        } else if (currentRoom) {
          const id = roomSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing room spot id for visibility toggle');
          }

          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${id}`,
            { isActive: nextActive },
          );
          const refreshed = await EventsService.getRoomSpots(
            currentRoom.roomId,
          );
          setRoomSpots(refreshed);
        }
        sendMessageToUnreal({
          action: 'SPOTS_UPDATED',
          spots: [],
        });
        setAutosaveStatus('saved');
      } catch (toggleError) {
        console.error(
          `Failed to toggle spot visibility: ${
            (toggleError as Error)?.message
          }`,
          {
            ueIdentifier:
              typeof spot.ueIdentifier === 'string' ? spot.ueIdentifier : null,
            brandingId: currentBrandingId,
            roomId: currentRoom?.roomId,
          },
        );
        setAutosaveStatus('error');
      }
    },
    [
      currentBrandingId,
      currentRoom,
      isBrandingModeEnabled,
      sendMessageToUnreal,
      setAutosaveStatus,
      setBrandingSpots,
      setRoomSpots,
    ],
  );

  const hotspots = useMemo<AnySpot[]>(
    () =>
      Array.isArray(brandingHotspots) ? (brandingHotspots as AnySpot[]) : [],
    [brandingHotspots],
  );

  // Shared with the sort below — mirrors the name fallback each row already
  // renders (spot.name, else the UE identifier, else "Unnamed").
  const getSpotDisplayName = useCallback(
    (spot: AnySpot): string => {
      if (typeof spot.name === 'string' && spot.name.length > 0) {
        return spot.name;
      }
      const ueId = normalizeUeIdentifier(spot.ueIdentifier);
      return ueId || translate('adminToolbox.unnamed');
    },
    [translate],
  );

  const getHotspotDisplayName = useCallback(
    (hotspot: AnySpot): string => {
      if (typeof hotspot.name === 'string' && hotspot.name.length > 0) {
        return hotspot.name;
      }
      const id =
        typeof (hotspot as { brandingHotspotId?: unknown })
          .brandingHotspotId === 'string'
          ? ((hotspot as { brandingHotspotId: string })
              .brandingHotspotId as string)
          : '';
      return id || translate('adminToolbox.unnamed', 'Unnamed');
    },
    [translate],
  );

  const sortedHotspots = useMemo(
    () => sortByName(hotspots, getHotspotDisplayName, sortDirections.hotspots),
    [hotspots, getHotspotDisplayName, sortDirections.hotspots],
  );

  const selectedHotspotId =
    selectedNode?.kind === EditorNodeKind.HOTSPOT
      ? selectedNode.hotspotId
      : null;

  // The catalogue of brandable entities is declared by the build and reported by
  // Unreal, never by the configuration — it lists what may be overridden, not
  // what is overridden.
  const { entities: brandingEntities, isAvailable: isObjectsFocusAvailable } =
    useBrandingEntityCatalogue();
  const selectedBrandingEntityId =
    selectedNode?.kind === EditorNodeKind.BRANDING_ENTITY
      ? selectedNode.entityId
      : null;

  // The runtime-spawnable type schema this build published. `available` is
  // false when the build ships no schema at all — the group then renders
  // nothing rather than an empty picker.
  const { types: spawnableTypes, available: spawnableObjectsAvailable } =
    useSpawnableObjects();
  const selectedRealtimeEntityId =
    selectedNode?.kind === EditorNodeKind.REALTIME_ENTITY
      ? selectedNode.entityId
      : null;
  const [addObjectAnchorEl, setAddObjectAnchorEl] =
    useState<HTMLElement | null>(null);

  // Once per registry change rather than per row per render: the dirty check
  // stringifies each entity twice, and the tree re-renders on every selection.
  const dirtyRealtimeEntityIds = useMemo(
    () =>
      new Set(
        realtimeEntities
          .filter(isRealtimeEntityDirty)
          .map((entity) => entity.entityId),
      ),
    [realtimeEntities],
  );

  // Dropped, not kept, when the group disappears with the menu open: the anchor
  // element unmounts with the group, and a retained one would reopen the menu
  // against a detached node when the group next renders.
  useEffect(() => {
    if (!spawnableObjectsAvailable) {
      setAddObjectAnchorEl(null);
    }
  }, [spawnableObjectsAvailable]);

  /**
   * Spawns one entity of the picked type and opens its inspector.
   *
   * `entityId` and `type` are sent, plus a seeded `TriggerName` for a region
   * type — the engine defaults the transform to origin/zero/unit — and the id
   * is always generated here because an entity spawned without one can never
   * be edited or deleted.
   */
  const handleSpawnObject = useCallback(
    (typeId: string, displayName: string) => {
      setAddObjectAnchorEl(null);

      const entityId = crypto.randomUUID();
      const schema = spawnableTypes.find(
        (spawnableType) => spawnableType.typeId === typeId,
      );
      const properties = seedSpawnProperties(schema, entityId);
      // A region type is labelled by its seeded trigger name so the Spawns row
      // and the AI-scripts row read the same. Otherwise a counter over
      // same-type rows keeps default labels tellable apart; the label is
      // hub-side only — the wire contract has no name field.
      const sameTypeCount = realtimeEntities.filter(
        (entity) => entity.typeId === typeId,
      ).length;
      const name =
        readTriggerName(properties) || `${displayName} ${sameTypeCount + 1}`;
      spawnRealtimeEntitiesInUnreal(sendMessageToUnreal, [
        { entityId, type: typeId, ...(properties ? { properties } : {}) },
      ]);
      upsertRealtimeEntity({
        entityId,
        typeId,
        displayName,
        name,
        ...(properties ? { properties } : {}),
      });
      selectRealtimeEntity(entityId);
    },
    [
      realtimeEntities,
      selectRealtimeEntity,
      sendMessageToUnreal,
      spawnableTypes,
      upsertRealtimeEntity,
    ],
  );

  /**
   * Creates a 3D-object hotspot draft and opens its inspector.
   *
   * Hotspot authoring is 3D-only (MV-1756): the record is created empty and the
   * user uploads a `.glb` in the inspector, which is what starts the in-world
   * placement. 2D panels are no longer authored here, but existing ones stay
   * editable.
   */
  const handleAddHotspot = useCallback(async () => {
    const brandingHotspotId = crypto.randomUUID();

    if (!currentBrandingId) {
      return;
    }

    const createPayload = {
      isActive: true,
      type: 'OBJECT_3D',
      name: translate('editorMode.newHotspot3D', 'New 3D object'),
      data: { url: '' },
    };

    setAutosaveStatus('saving');
    try {
      let created;
      try {
        created = await BrandingsService.createBrandingHotspot(
          currentBrandingId,
          { brandingHotspotId, ...createPayload },
        );
      } catch (createError) {
        const status = (createError as { response?: { status?: number } })
          ?.response?.status;
        if (status !== 400) {
          throw createError;
        }
        console.warn(
          'Backend rejected client-supplied brandingHotspotId, retrying with a server-generated id',
          { brandingId: currentBrandingId, brandingHotspotId },
        );
        created = await BrandingsService.createBrandingHotspot(
          currentBrandingId,
          createPayload,
        );
      }

      const createdId =
        typeof created?.brandingHotspotId === 'string'
          ? created.brandingHotspotId
          : brandingHotspotId;
      const refreshed = await BrandingsService.getBrandingHotspots(
        currentBrandingId,
        { limit: 200 },
      );
      setBrandingHotspots(refreshed.hotspots ?? []);
      setPendingHotspotPlacement({
        brandingHotspotId: createdId,
        isCreated: true,
      });
      setAutosaveStatus('saved');
      selectHotspot(createdId);
    } catch (createError) {
      console.error(
        `Failed to create 3D hotspot draft: ${(createError as Error)?.message}`,
        { brandingId: currentBrandingId, brandingHotspotId },
      );
      setAutosaveStatus('error');
    }
  }, [
    currentBrandingId,
    setPendingHotspotPlacement,
    setBrandingHotspots,
    setAutosaveStatus,
    selectHotspot,
    translate,
  ]);

  const handleToggleHotspotVisibility = useCallback(
    async (e: React.MouseEvent, hotspot: AnySpot) => {
      e.stopPropagation();
      if (!currentBrandingId) {
        return;
      }
      const id =
        typeof (hotspot as { brandingHotspotId?: unknown })
          .brandingHotspotId === 'string'
          ? ((hotspot as { brandingHotspotId: string })
              .brandingHotspotId as string)
          : null;
      if (!id) {
        return;
      }
      const nextActive = hotspot.isActive === false;
      setAutosaveStatus('saving');
      try {
        await BrandingsService.patchBrandingHotspot(currentBrandingId, id, {
          isActive: nextActive,
        });
        const refreshed = await BrandingsService.getBrandingHotspots(
          currentBrandingId,
          { limit: 200 },
        );
        setBrandingHotspots(refreshed.hotspots ?? []);
        sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
        setAutosaveStatus('saved');
      } catch (toggleError) {
        console.error(
          `Failed to toggle hotspot visibility: ${
            (toggleError as Error)?.message
          }`,
          { brandingId: currentBrandingId, brandingHotspotId: id },
        );
        setAutosaveStatus('error');
      }
    },
    [
      currentBrandingId,
      sendMessageToUnreal,
      setAutosaveStatus,
      setBrandingHotspots,
    ],
  );

  const currentSortListId = getSortableListIdForFocus(activeFocus);
  const isCurrentSortListEmpty = getIsCurrentSortListEmpty(
    currentSortListId,
    spots.length,
    spotDrift.rows.length,
    hotspots.length,
    brandingEntities.length,
    isAiScriptsListEmpty,
  );

  if (isSceneTreeCollapsed) {
    return (
      <Box
        sx={[styles.glassPanel, styles.sceneTreeCollapsedPill]}
        onClick={toggleSceneTree}
        data-testid="scene-tree-expand-pill"
      >
        <ViewSidebarOutlinedIcon fontSize="small" />
        <Typography sx={{ fontSize: '13px', color: 'inherit' }}>
          {translate('editorMode.scene')}
        </Typography>
      </Box>
    );
  }

  const visibleGroupIds = FOCUS_TO_GROUP_IDS[activeFocus];

  const renderEnvironment = () => (
    <Box key={EditorGroupId.ENVIRONMENT} sx={treeStyles.groupSection}>
      <Box sx={treeStyles.groupLabelRow}>
        <Typography sx={treeStyles.groupLabel}>
          {translate('editorMode.groupEnvironment')}
        </Typography>
      </Box>
      <Box
        sx={[treeStyles.node, isMapColorsSelected && treeStyles.nodeSelected]}
        onClick={selectMapAndColors}
        data-testid="scene-tree-map-colors-node"
      >
        <PaletteOutlinedIcon sx={treeStyles.nodeIcon} />
        <Typography sx={treeStyles.nodeLabel}>
          {translate('editorMode.mapAndColors')}
        </Typography>
      </Box>
      <Typography sx={treeStyles.driftNote}>
        {translate('editorMode.driftEnvironmentNote')}
      </Typography>
    </Box>
  );

  const renderCoverageMarker = (ueId: string) => {
    const row = driftByUe.get(normalizeUeIdentifier(ueId));
    if (!row) {
      return null;
    }
    if (row.status === 'configured') {
      return (
        <CheckCircleOutlineOutlinedIcon
          sx={[treeStyles.coverageMarker, treeStyles.coverageConfigured]}
        />
      );
    }
    if (row.status === 'placeholder') {
      return (
        <RadioButtonUncheckedOutlinedIcon
          sx={[treeStyles.coverageMarker, treeStyles.coveragePlaceholder]}
        />
      );
    }
    return null;
  };

  // An anchor the streamed build offers that this configuration has not filled yet. Selecting it
  // opens the Inspector's "Add to configuration" — the editor's only way to author a spot for a new
  // anchor, which is why this half of the build diff stays after the warning half was dropped.
  const renderGhostSpotNode = (
    row: IBrandingSpotDriftRow<IBrandingSpotApi>,
  ) => {
    const ueId = normalizeUeIdentifier(row.ueIdentifier);
    const sourceSpot = { ueIdentifier: ueId, type: row.type } as AnySpot;
    const type = getSpotType(sourceSpot);
    const isSelected =
      !!selectedSpotUeId && ueId === normalizeUeIdentifier(selectedSpotUeId);

    return (
      <Box
        key={`missing:${ueId}`}
        ref={isSelected ? selectedNodeRef : undefined}
        sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
        onClick={() => ueId && handleSpotSelect(sourceSpot, ueId)}
        data-testid={`scene-tree-missing-row-${ueId}`}
        aria-selected={isSelected}
      >
        {getSpotTypeIcon(type, treeStyles.nodeIcon)}
        <Box sx={treeStyles.ghostNodeLabel}>
          <Box sx={treeStyles.ghostNodeTitleRow}>
            <Typography sx={treeStyles.ghostNodeTitle}>{ueId}</Typography>
            {row.isNew ? (
              <Box component="span" sx={treeStyles.newBadge}>
                {translate('editorMode.driftNew')}
              </Box>
            ) : null}
          </Box>
          <Typography sx={treeStyles.ghostNodeSub}>
            {row.regionName || translate('editorMode.driftInBuild')}
          </Typography>
        </Box>
        <AddCircleOutlineOutlinedIcon sx={treeStyles.ghostMarkerMissing} />
      </Box>
    );
  };

  // Mirrors renderGhostSpotNode's own name resolution, so drift rows sort by
  // the same text they display.
  const getDriftRowDisplayName = (
    row: IBrandingSpotDriftRow<IBrandingSpotApi>,
  ): string => normalizeUeIdentifier(row.ueIdentifier);

  const renderSpots = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.SPOTS];

    const missingRows = sortByName(
      spotDrift.rows.filter((row) => row.status === 'missing'),
      getDriftRowDisplayName,
      sortDirections.spots,
    );
    // A spot whose anchor the streamed build no longer declares is dropped from the list rather than
    // called out: it renders nothing in the world, and the red "removed from build" group it used to
    // get was an alarm about the build, raised at someone who can only author the configuration. It
    // stays in the configuration untouched and comes back the moment a build declares that anchor
    // again.
    const orphanUeSet = new Set(
      spotDrift.rows
        .filter((row) => row.status === 'orphaned')
        .map((row) => normalizeUeIdentifier(row.ueIdentifier)),
    );
    const inConfigSpots = sortByName(
      spots.filter(
        (spot) => !orphanUeSet.has(normalizeUeIdentifier(spot.ueIdentifier)),
      ),
      getSpotDisplayName,
      sortDirections.spots,
    );

    return (
      <React.Fragment key={EditorGroupId.SPOTS}>
        <Box sx={treeStyles.groupSection}>
          <Box
            sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
            onClick={() => toggleGroup(EditorGroupId.SPOTS)}
            data-testid="scene-tree-spots-group-header"
          >
            <ExpandMoreOutlinedIcon
              sx={[
                treeStyles.groupChevron,
                isCollapsed && treeStyles.groupChevronCollapsed,
              ]}
            />
            <Typography sx={treeStyles.groupLabel}>
              {translate('editorMode.groupSpots')}
            </Typography>
            {inConfigSpots.length > 0 && (
              <Typography sx={treeStyles.groupCount}>
                {inConfigSpots.length}
              </Typography>
            )}
          </Box>

          {!isCollapsed && inConfigSpots.length === 0 && (
            <Typography sx={treeStyles.emptyText}>
              {translate('editorMode.noSpots')}
            </Typography>
          )}

          {!isCollapsed &&
            inConfigSpots.map((spot, idx) => {
              const ueId = normalizeUeIdentifier(spot.ueIdentifier);
              const key = ueId || `spot-${idx}`;
              const type = getSpotType(spot);
              const isSelected =
                !!selectedSpotUeId &&
                ueId === normalizeUeIdentifier(selectedSpotUeId);
              const isHidden = spot.isActive === false;
              const name =
                typeof spot.name === 'string' && spot.name.length > 0
                  ? spot.name
                  : ueId || translate('adminToolbox.unnamed');
              // Two independent images per row, so a failure of one must not
              // blank the other — hence the suffixed failure keys.
              const locationKey = `${key}:location`;
              const contentKey = `${key}:content`;
              const locationThumbUrl = failedThumbs[locationKey]
                ? null
                : getSpotLocationPreviewUrl(spot);
              const contentThumbUrl = failedThumbs[contentKey]
                ? null
                : getSpotThumbnailUrl(spot);

              return (
                <Tooltip
                  key={key}
                  title={name}
                  placement="right"
                  enterDelay={350}
                  arrow
                >
                  <Box
                    ref={isSelected ? selectedNodeRef : undefined}
                    sx={[
                      treeStyles.node,
                      isSelected && treeStyles.nodeSelected,
                    ]}
                    onClick={() => ueId && handleSpotSelect(spot, ueId)}
                    data-testid={`scene-tree-spot-row-${ueId}`}
                    aria-selected={isSelected}
                  >
                    {locationThumbUrl ? (
                      <Box
                        component="img"
                        src={locationThumbUrl}
                        alt=""
                        sx={treeStyles.nodeThumb}
                        data-testid={`scene-tree-spot-row-${ueId}-location-thumb`}
                        onError={() => {
                          setFailedThumbs((prev) => ({
                            ...prev,
                            [locationKey]: true,
                          }));
                        }}
                      />
                    ) : (
                      getSpotTypeIcon(type, treeStyles.nodeIcon)
                    )}
                    <Box sx={treeStyles.nodeLabelRow}>
                      <Typography sx={treeStyles.nodeLabel}>{name}</Typography>
                      {contentThumbUrl ? (
                        <Box
                          component="img"
                          src={contentThumbUrl}
                          alt=""
                          sx={treeStyles.nodeContentThumb}
                          data-testid={`scene-tree-spot-row-${ueId}-content-thumb`}
                          onError={() => {
                            setFailedThumbs((prev) => ({
                              ...prev,
                              [contentKey]: true,
                            }));
                          }}
                        />
                      ) : null}
                    </Box>
                    {renderCoverageMarker(ueId)}
                    <Box sx={treeStyles.nodeActions}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleVisibility(e, spot)}
                        sx={[
                          treeStyles.nodeEye,
                          isHidden && treeStyles.nodeEyeHidden,
                        ]}
                        aria-label={translate(
                          'editorMode.toggleVisibility',
                          'Toggle visibility',
                        )}
                        data-testid={`scene-tree-spot-row-${ueId}-visibility-btn`}
                      >
                        {isHidden ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
        </Box>

        {missingRows.length > 0 && (
          <Box sx={treeStyles.groupSection}>
            <Box
              sx={treeStyles.groupLabelRow}
              data-testid="scene-tree-missing-group-header"
            >
              <Typography
                sx={[treeStyles.groupLabel, treeStyles.driftGroupLabelAmber]}
              >
                {translate('editorMode.driftMissingGroup')}
              </Typography>
              <Typography sx={treeStyles.groupCount}>
                {missingRows.length}
              </Typography>
            </Box>
            {missingRows.map(renderGhostSpotNode)}
          </Box>
        )}
      </React.Fragment>
    );
  };

  const renderHotspots = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.HOTSPOTS];

    return (
      <Box key={EditorGroupId.HOTSPOTS} sx={treeStyles.groupSection}>
        <Box
          sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
          onClick={() => toggleGroup(EditorGroupId.HOTSPOTS)}
        >
          <ExpandMoreOutlinedIcon
            sx={[
              treeStyles.groupChevron,
              isCollapsed && treeStyles.groupChevronCollapsed,
            ]}
          />
          <Typography sx={treeStyles.groupLabel}>
            {translate('editorMode.groupHotspots', 'Hotspots')}
          </Typography>
          {hotspots.length > 0 && (
            <Typography sx={treeStyles.groupCount}>
              {hotspots.length}
            </Typography>
          )}
        </Box>

        {!isCollapsed && (
          <Box
            component="button"
            type="button"
            onClick={handleAddHotspot}
            data-testid="scene-tree-add-hotspot-btn"
            sx={treeStyles.groupAddButton}
          >
            <AddCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
            {translate('editorMode.addHotspot', 'Add hotspot')}
          </Box>
        )}

        {!isCollapsed && hotspots.length === 0 && (
          <Typography sx={treeStyles.emptyText}>
            {translate(
              'editorMode.noHotspots',
              'No hotspots in this configuration',
            )}
          </Typography>
        )}

        {!isCollapsed &&
          sortedHotspots.map((hotspot, idx) => {
            const id =
              typeof (hotspot as { brandingHotspotId?: unknown })
                .brandingHotspotId === 'string'
                ? ((hotspot as { brandingHotspotId: string })
                    .brandingHotspotId as string)
                : '';
            const key = id || `hotspot-${idx}`;
            const is3D =
              String(hotspot.type ?? '').toUpperCase() === 'OBJECT_3D';
            const isSelected = !!selectedHotspotId && id === selectedHotspotId;
            const isHidden = hotspot.isActive === false;
            const name =
              typeof hotspot.name === 'string' && hotspot.name.length > 0
                ? hotspot.name
                : id || translate('adminToolbox.unnamed', 'Unnamed');

            return (
              <Tooltip
                key={key}
                title={name}
                placement="right"
                enterDelay={350}
                arrow
              >
                <Box
                  sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
                  onClick={() => id && selectHotspot(id)}
                >
                  {is3D ? (
                    <ExtensionOutlinedIcon sx={treeStyles.nodeIcon} />
                  ) : (
                    <ImageOutlinedIcon sx={treeStyles.nodeIcon} />
                  )}
                  <Typography sx={treeStyles.nodeLabel}>{name}</Typography>
                  <Box sx={treeStyles.nodeActions}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleToggleHotspotVisibility(e, hotspot)}
                      sx={[
                        treeStyles.nodeEye,
                        isHidden && treeStyles.nodeEyeHidden,
                      ]}
                      aria-label={translate(
                        'editorMode.toggleVisibility',
                        'Toggle visibility',
                      )}
                    >
                      {isHidden ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
      </Box>
    );
  };

  /**
   * Renders the live-spawned entities group: the type-picker menu and one
   * selectable row per entity the session has spawned.
   */
  const renderSpawnedObjects = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.REALTIME_OBJECTS];

    return (
      <Box key={EditorGroupId.REALTIME_OBJECTS} sx={treeStyles.groupSection}>
        <Box
          sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
          onClick={() => toggleGroup(EditorGroupId.REALTIME_OBJECTS)}
        >
          <ExpandMoreOutlinedIcon
            sx={[
              treeStyles.groupChevron,
              isCollapsed && treeStyles.groupChevronCollapsed,
            ]}
          />
          <Typography sx={treeStyles.groupLabel}>
            {translate('editorMode.groupSpawnedObjects', 'Spawned objects')}
          </Typography>
          {realtimeEntities.length > 0 && (
            <Typography sx={treeStyles.groupCount}>
              {realtimeEntities.length}
            </Typography>
          )}
        </Box>

        {!isCollapsed && (
          <>
            <Box
              component="button"
              type="button"
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                setAddObjectAnchorEl(e.currentTarget)
              }
              data-testid="scene-tree-add-object-btn"
              sx={treeStyles.groupAddButton}
            >
              <AddCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
              {translate('editorMode.addObject', 'Add object')}
            </Box>
            <Menu
              anchorEl={addObjectAnchorEl}
              open={!!addObjectAnchorEl}
              onClose={() => setAddObjectAnchorEl(null)}
              PaperProps={{ sx: treeStyles.addObjectMenuPaper }}
            >
              {spawnableTypes.map((spawnableType) => (
                <MenuItem
                  key={spawnableType.typeId}
                  data-testid={`realtime-entity-spawn-option-${spawnableType.typeId}`}
                  onClick={() =>
                    handleSpawnObject(
                      spawnableType.typeId,
                      spawnableType.displayName,
                    )
                  }
                >
                  {spawnableType.displayName}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {!isCollapsed && realtimeEntities.length === 0 && (
          <Typography sx={treeStyles.emptyText}>
            {translate(
              'editorMode.noSpawnedObjects',
              'No objects spawned in this session',
            )}
          </Typography>
        )}

        {!isCollapsed &&
          realtimeEntities.map((entity) => {
            const isSelected =
              !!selectedRealtimeEntityId &&
              entity.entityId === selectedRealtimeEntityId;

            return (
              <Tooltip
                key={entity.entityId}
                title={entity.name}
                placement="right"
                enterDelay={350}
                arrow
              >
                <Box
                  sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
                  onClick={() => selectRealtimeEntity(entity.entityId)}
                  data-testid={`realtime-entity-row-${entity.entityId}`}
                >
                  <ViewInArOutlinedIcon sx={treeStyles.nodeIcon} />
                  <Typography sx={treeStyles.nodeLabel}>
                    {entity.name}
                  </Typography>
                  {dirtyRealtimeEntityIds.has(entity.entityId) && (
                    <Box component="span" sx={treeStyles.nodeDirtyDot} />
                  )}
                </Box>
              </Tooltip>
            );
          })}
      </Box>
    );
  };

  // The walk itself lives here now — see LayoutTree. What used to be here was the room *kit*, as a
  // facet tree, plus a single `Layout` row standing in for the whole chain; the kit moved into a
  // picker that opens over the stream (the facets stopped identifying a corridor — see
  // mapEditorRoots), and the chain came out of the right-hand panel and into the tree, where every
  // other thing in the scene is already listed one by one.
  const renderMapEditor = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.MAP_EDITOR];

    return (
      <Box key={EditorGroupId.MAP_EDITOR} sx={treeStyles.groupSection}>
        <Box
          sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
          onClick={() => toggleGroup(EditorGroupId.MAP_EDITOR)}
        >
          <ExpandMoreOutlinedIcon
            sx={[
              treeStyles.groupChevron,
              isCollapsed && treeStyles.groupChevronCollapsed,
            ]}
          />
          <Typography sx={treeStyles.groupLabel}>
            {translate('editorMode.groupMapEditor', 'Map editor')}
          </Typography>
          {layoutRooms.length > 0 && (
            <Typography sx={treeStyles.groupCount}>
              {layoutRooms.length}
            </Typography>
          )}
        </Box>

        {/* The way back to the panel after every segment has been deselected. A node row rather
            than a header button, matching "Map and colours" in the environment group: the group
            header is the collapse toggle everywhere in this tree, and overloading it here would
            break that.

            It no longer *contains* the layout — it is the row that says how long the walk is and
            opens the panel that explains what to do with it. */}
        {!isCollapsed && (
          <Box
            sx={[
              treeStyles.node,
              isMapLayoutSelected && treeStyles.nodeSelected,
            ]}
            onClick={selectMapLayout}
            data-testid="scene-tree-map-layout-node"
          >
            <ViewQuiltOutlinedIcon sx={treeStyles.nodeIcon} />
            <Typography sx={treeStyles.nodeLabel}>
              {translate('editorMode.mapEditorOpenPanel', 'Layout')}
            </Typography>
            {layoutRooms.length > 0 && (
              <Box sx={treeStyles.nodeActions}>
                <Typography sx={treeStyles.groupCount}>
                  {layoutRooms.length}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {!isCollapsed && <LayoutTree />}
      </Box>
    );
  };

  const renderObjects = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.OBJECTS];
    const sortedBrandingEntities = sortByName(
      brandingEntities,
      (entity) => entity.displayName || entity.entityId,
      sortDirections.objects,
    );

    return (
      <Box key={EditorGroupId.OBJECTS} sx={treeStyles.groupSection}>
        <Box
          sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
          onClick={() => toggleGroup(EditorGroupId.OBJECTS)}
          data-testid="scene-tree-objects-group-header"
        >
          <ExpandMoreOutlinedIcon
            sx={[
              treeStyles.groupChevron,
              isCollapsed && treeStyles.groupChevronCollapsed,
            ]}
          />
          <Typography sx={treeStyles.groupLabel}>
            {translate('editorMode.groupObjects', 'Objects')}
          </Typography>
          <Typography sx={treeStyles.groupCount}>
            {brandingEntities.length}
          </Typography>
          <ObjectsCsvActions entities={brandingEntities} />
        </Box>

        {!isCollapsed &&
          sortedBrandingEntities.map((entity) => {
            const isSelected = entity.entityId === selectedBrandingEntityId;
            const name = entity.displayName || entity.entityId;
            // One entity can live on several maps and shares a single value
            // set, so the maps it appears on are part of the row. An object that
            // is standing right now says so ahead of them — for one spawned into
            // the world that is the only thing there is to say, since it belongs
            // to no level of the build.
            const subtitle = [
              entity.isLive
                ? translate('editorMode.brandingEntityInWorld', 'In world')
                : '',
              entity.maps.join(' · '),
            ]
              .filter(Boolean)
              .join(' · ');

            return (
              <Tooltip
                key={entity.entityId}
                title={name}
                placement="right"
                enterDelay={350}
                arrow
              >
                <Box
                  sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
                  onClick={() => selectBrandingEntity(entity.entityId)}
                  data-testid={`scene-tree-object-row-${entity.entityId}`}
                >
                  <CategoryOutlinedIcon sx={treeStyles.nodeIcon} />
                  <Box sx={treeStyles.ghostNodeLabel}>
                    <Typography sx={treeStyles.ghostNodeTitle}>
                      {name}
                    </Typography>
                    <Typography sx={treeStyles.ghostNodeSub}>
                      {subtitle}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
      </Box>
    );
  };

  return (
    // The wrap is a column of two things now: the Scene panel, and the layout plaque under it. They
    // were one Box while the panel was the only thing on this side; the plaque has to be *outside*
    // the glass, because it is about the whole layout and the panel above it is a list of parts.
    <Box sx={styles.sceneTreeWrap}>
      <Box sx={[styles.glassPanel, styles.sceneTreePanel]}>
        <Box sx={styles.panelHeader}>
          <Box sx={styles.panelTitleRow}>
            <LayersOutlinedIcon sx={styles.panelTitleIcon} />
            <Typography sx={styles.panelTitle}>
              {translate('editorMode.scene')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            {currentSortListId && !isCurrentSortListEmpty && (
              <SortToggleButton
                direction={sortDirections[currentSortListId]}
                onToggle={() => toggleSortDirection(currentSortListId)}
                testId="scene-tree-sort-direction-btn"
              />
            )}
            <IconButton
              onClick={toggleSceneTree}
              sx={styles.panelIconButton}
              size="small"
              aria-label={translate(
                'editorMode.collapseTree',
                'Collapse scene',
              )}
              data-testid="scene-tree-collapse-btn"
            >
              <ViewSidebarOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={[
            treeStyles.railLayout,
            isSpotMediaUploadLocked && treeStyles.railLayoutLocked,
          ]}
        >
          <Box sx={treeStyles.rail} role="tablist">
            <RailButton
              active={activeFocus === 'environment'}
              icon={<PublicOutlinedIcon />}
              label={translate('editorMode.railMap')}
              tooltip={translate('editorMode.focusEnvironment')}
              onClick={() => setActiveFocus('environment')}
              testId="scene-tree-focus-environment-tab"
            />
            <RailButton
              active={activeFocus === 'spots'}
              icon={<ImageOutlinedIcon />}
              label={translate('editorMode.railSpots')}
              tooltip={translate('editorMode.focusSpots')}
              onClick={() => setActiveFocus('spots')}
              testId="scene-tree-focus-spots-tab"
            />
            <RailButton
              active={activeFocus === 'hotspots'}
              icon={<PlaceOutlinedIcon />}
              label={translate('editorMode.railHotspots', 'Hotspots')}
              tooltip={translate('editorMode.focusHotspots', 'Hotspots')}
              onClick={() => setActiveFocus('hotspots')}
              testId="scene-tree-focus-hotspots-tab"
            />
            {/* Same absence rule as the map editor below: no schema on the streamed
                build means UNKNOWN, so the tab has nothing to offer and is not shown. */}
            {spawnableObjectsAvailable && (
              <RailButton
                active={activeFocus === 'spawnables'}
                icon={<ViewInArOutlinedIcon />}
                label={translate('editorMode.railSpawnables', 'Spawns')}
                tooltip={translate(
                  'editorMode.focusSpawnables',
                  'Spawned objects',
                )}
                onClick={() => setActiveFocus('spawnables')}
                testId="scene-tree-focus-spawnables-tab"
              />
            )}
            <RailButton
              active={activeFocus === 'assistant'}
              icon={<SmartToyOutlinedIcon />}
              label={translate('editorMode.railScripts')}
              tooltip={translate('editorMode.focusAssistant')}
              onClick={() => setActiveFocus('assistant')}
              testId="scene-tree-focus-assistant-tab"
            />
            {/* Absent, not disabled: a map whose build ships no room kit has nothing to
              offer and nothing to explain, so the rail simply does not have this tab. */}
            {canUseMapEditor ? (
              <RailButton
                active={activeFocus === 'mapEditor'}
                icon={<ViewQuiltOutlinedIcon />}
                label={translate('editorMode.railMapEditor', 'Layout')}
                tooltip={translate('editorMode.focusMapEditor', 'Map editor')}
                onClick={() => setActiveFocus('mapEditor')}
                testId="scene-tree-focus-map-editor-tab"
              />
            ) : null}
            {isObjectsFocusAvailable && (
              <RailButton
                active={activeFocus === 'objects'}
                icon={<CategoryOutlinedIcon />}
                label={translate('editorMode.railObjects', 'Objects')}
                tooltip={translate('editorMode.focusObjects', 'Objects')}
                onClick={() => setActiveFocus('objects')}
                testId="scene-tree-focus-objects-tab"
              />
            )}
          </Box>

          <Box sx={treeStyles.railBody}>
            {visibleGroupIds.includes(EditorGroupId.ENVIRONMENT) &&
              renderEnvironment()}
            {visibleGroupIds.includes(EditorGroupId.SPOTS) && renderSpots()}
            {visibleGroupIds.includes(EditorGroupId.HOTSPOTS) &&
              renderHotspots()}
            {spawnableObjectsAvailable &&
              visibleGroupIds.includes(EditorGroupId.REALTIME_OBJECTS) &&
              renderSpawnedObjects()}
            {visibleGroupIds.includes(EditorGroupId.AI_SCRIPTS) && (
              <AiScriptsTreeSection />
            )}
            {canUseMapEditor &&
              visibleGroupIds.includes(EditorGroupId.MAP_EDITOR) &&
              renderMapEditor()}
            {visibleGroupIds.includes(EditorGroupId.OBJECTS) &&
              isObjectsFocusAvailable &&
              renderObjects()}
          </Box>
        </Box>
      </Box>

      {/* Under the panel, and only on the focus it belongs to. Publish and Undo all are about the
          whole layout, so they are not in the segment panel — a Publish button inside a panel titled
          with one room's name reads as publishing that room. The layout's status is not here at all:
          it is on the BrandingBar with everything else's, since it saves to the same record. */}
      {canUseMapEditor && activeFocus === 'mapEditor' && <LayoutPlaque />}
    </Box>
  );
};

export default SceneTree;
